import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useUI } from '../contexts/UIProvider';
import * as api from '../services/apiService';
import VideoIcon from './icons/VideoIcon';
import PlayIcon from './icons/PlayIcon';
import CloseIcon from './icons/CloseIcon';
import MicrophoneIcon from './icons/MicrophoneIcon';
import AmplifierIcon from './icons/AmplifierIcon';
import TrashIcon from './icons/TrashIcon';
import PlusCircleIcon from './icons/PlusCircleIcon';
import ShareIcon from './icons/ShareIcon';
import type { Session } from '../types';

type SessionTab = 'solo' | 'base' | 'collab';

const MAX_RECORDING_SECONDS = 40;

const Watermark: React.FC = () => (
    <div className="absolute top-4 left-4 z-40 flex items-center gap-2 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10 pointer-events-none">
        <AmplifierIcon className="h-5 w-5 text-brand-orange" />
        <span className="text-sm font-extrabold text-white tracking-tight">beYourAMP</span>
    </div>
);

const SessionsPage: React.FC = () => {
    const { currentUser } = useAuth();
    const { showToast } = useUI();
    const [sessions, setSessions] = useState<Session[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<SessionTab>('solo');
    
    const [showRecordModal, setShowRecordModal] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [countdown, setCountdown] = useState<number | null>(null);
    const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [performanceType, setPerformanceType] = useState<'solo' | 'base'>('solo');
    const [layout] = useState<'split-v' | 'split-h'>('split-v');
    const [isUploading, setIsUploading] = useState(false);

    const [parentSession, setParentSession] = useState<Session | null>(null);
    const [playingSession, setPlayingSession] = useState<Session | null>(null);

    const videoPreviewRef = useRef<HTMLVideoElement>(null);
    const reviewVideoRef = useRef<HTMLVideoElement>(null);
    const baseVideoRef = useRef<HTMLVideoElement>(null);
    const recorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const streamRef = useRef<MediaStream | null>(null);
    const timerRef = useRef<any>(null);

    useEffect(() => {
        if (recordedBlob) {
            const url = URL.createObjectURL(recordedBlob);
            setPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        } else {
            setPreviewUrl(null);
        }
    }, [recordedBlob]);

    useEffect(() => {
        if (isPreviewing && previewUrl && reviewVideoRef.current) {
            reviewVideoRef.current.load();
        }
    }, [isPreviewing, previewUrl]);

    // LOGICA DI SINCRONIZZAZIONE REVIEW
    useEffect(() => {
        const reviewEl = reviewVideoRef.current;
        const baseEl = baseVideoRef.current;

        if (isPreviewing && reviewEl && baseEl) {
            const handlePlay = () => baseEl.play().catch(() => {});
            const handlePause = () => baseEl.pause();
            const handleSeek = () => { baseEl.currentTime = reviewEl.currentTime; };

            reviewEl.addEventListener('play', handlePlay);
            reviewEl.addEventListener('pause', handlePause);
            reviewEl.addEventListener('seeking', handleSeek);
            reviewEl.addEventListener('seeked', handleSeek);

            return () => {
                reviewEl.removeEventListener('play', handlePlay);
                reviewEl.removeEventListener('pause', handlePause);
                reviewEl.removeEventListener('seeking', handleSeek);
                reviewEl.removeEventListener('seeked', handleSeek);
            };
        }
    }, [isPreviewing, previewUrl]);

    const fetchSessions = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await api.getSessions();
            setSessions(data);
        } catch (e) {
            console.error("Failed to fetch sessions", e);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
    }, []);

    const startCamera = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ 
                video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 1280 }, frameRate: { ideal: 30 } }, 
                audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false, sampleRate: 48000, channelCount: 2 }
            });
            streamRef.current = stream;
            setTimeout(() => {
                if (videoPreviewRef.current) videoPreviewRef.current.srcObject = stream;
            }, 150);
        } catch (err) {
            showToast("Camera access denied.");
            setShowRecordModal(false);
        }
    }, [showToast]);

    const stopRecordingInternal = useCallback(() => {
        if (recorderRef.current && recorderRef.current.state === 'recording') {
            recorderRef.current.stop();
            if (baseVideoRef.current) {
                baseVideoRef.current.pause();
                baseVideoRef.current.currentTime = 0;
            }
            setIsRecording(false);
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        }
    }, []);

    const executeStartRecording = useCallback(() => {
        if (!streamRef.current) return;
        chunksRef.current = [];
        setRecordingSeconds(0);
        
        const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') ? 'video/webm;codecs=vp9,opus' : 'video/mp4';
        const recorder = new MediaRecorder(streamRef.current, { mimeType, videoBitsPerSecond: 2500000, audioBitsPerSecond: 128000 });
        
        recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
        recorder.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: mimeType });
            setRecordedBlob(blob);
            setIsPreviewing(true);
            stopCamera();
        };

        if (baseVideoRef.current) {
            baseVideoRef.current.currentTime = 0;
            baseVideoRef.current.play().catch(() => {});
        }

        recorderRef.current = recorder;
        recorder.start(200);
        setIsRecording(true);

        timerRef.current = setInterval(() => {
            setRecordingSeconds(prev => {
                if (prev >= MAX_RECORDING_SECONDS - 1) {
                    stopRecordingInternal();
                    showToast("Max duration reached");
                    return MAX_RECORDING_SECONDS;
                }
                return prev + 1;
            });
        }, 1000);
    }, [stopCamera, stopRecordingInternal, showToast]);

    const handleStartRecording = () => {
        if (isRecording || countdown !== null) return;
        let count = 3;
        setCountdown(count);
        const startTimer = setInterval(() => {
            count -= 1;
            if (count === 0) { clearInterval(startTimer); setCountdown(null); executeStartRecording(); }
            else setCountdown(count);
        }, 1000);
    };

    const handleRetake = () => {
        setRecordedBlob(null);
        setIsPreviewing(false);
        setRecordingSeconds(0);
        startCamera();
    };

    const handleJamNow = (session: Session) => {
        setPlayingSession(null); 
        setParentSession(session);
        setPerformanceType('solo'); 
        setShowRecordModal(true);
        startCamera();
    };

    const handlePublish = async () => {
        if (!recordedBlob || !title || !currentUser) return;
        setIsUploading(true);
        try {
            await api.publishSession({
                videoBlob: recordedBlob,
                fileName: `${currentUser.handle}_${Date.now()}.webm`,
                title, description,
                type: parentSession ? 'collab' : performanceType,
                layout, parentSessionId: parentSession?.id
            });
            showToast(`Broadcast published!`);
            handleCloseModal();
            fetchSessions();
        } catch (e: any) {
            alert(`Broadcast failed: ${e.message}`);
        } finally { setIsUploading(false); }
    };

    /**
     * Handles the deletion of a session.
     */
    const handleDelete = async (e: React.MouseEvent, sessionId: string) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to delete this session?')) {
            try {
                await api.deleteSession(sessionId);
                showToast("Session deleted successfully.");
                fetchSessions();
            } catch (err) {
                showToast("Failed to delete session.");
            }
        }
    };

    const handleShareSession = async (e: React.MouseEvent, session: Session) => {
        e.stopPropagation();
        const shareUrl = `${window.location.origin}/?session=${session.id}`;
        const text = `🔥 Check out this session on beYourAMP: "${session.title}" by @${session.creator.handle}`;
        try {
            if (navigator.share) {
                await navigator.share({ title: 'beYourAMP Session', text, url: shareUrl });
            } else {
                await navigator.clipboard.writeText(shareUrl);
                showToast("Link copied!");
            }
        } catch (err) {}
    };

    const handleCloseModal = () => {
        if (timerRef.current) clearInterval(timerRef.current);
        setShowRecordModal(false);
        setRecordedBlob(null);
        setParentSession(null);
        setIsPreviewing(false);
        setRecordingSeconds(0);
        setTitle('');
        setDescription('');
        setCountdown(null);
        stopCamera();
    };

    return (
        <div className="space-y-6 font-urbanist pb-20">
            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-3xl font-black text-white uppercase tracking-tighter">Recording Stage</h2>
                    <p className="text-brand-muted text-sm uppercase font-bold tracking-widest mt-1">HD 9:16 • Direct Storage</p>
                </div>
                <button onClick={() => { setShowRecordModal(true); startCamera(); }} className="bg-brand-orange text-white font-black px-8 py-3 rounded-full hover:bg-orange-600 transition-all text-xs uppercase tracking-[0.2em] shadow-lg shadow-brand-orange/20 flex items-center gap-2">
                    <MicrophoneIcon className="h-5 w-5" />
                    <span>Enter Studio</span>
                </button>
            </div>

            <div className="flex bg-gray-900/50 p-1 rounded-2xl border border-gray-800">
                {(['solo', 'base', 'collab'] as SessionTab[]).map(tab => (
                    <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab ? 'bg-brand-orange text-white shadow-lg' : 'text-brand-muted hover:text-white'}`}>
                        {tab === 'solo' ? 'Solo Acts' : tab === 'base' ? 'Jam Bases' : 'Collabs'}
                    </button>
                ))}
            </div>

            {isLoading ? (
                <div className="p-20 text-center animate-pulse text-brand-muted uppercase font-black tracking-widest">Loading stage...</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sessions.filter(s => s.type === activeTab).map(session => (
                        <SessionCard key={session.id} session={session} onDelete={(e) => handleDelete(e, session.id)} currentUserHandle={currentUser?.handle} onPlay={() => setPlayingSession(session)} onJam={() => handleJamNow(session)} onShare={(e) => handleShareSession(e, session)} />
                    ))}
                    {sessions.filter(s => s.type === activeTab).length === 0 && (
                        <div className="col-span-full py-20 text-center border-2 border-dashed border-gray-800 rounded-2xl">
                             <VideoIcon className="h-16 w-16 text-gray-700 mx-auto mb-4 opacity-30" />
                             <p className="text-brand-muted font-bold uppercase tracking-widest">No sessions in this category.</p>
                        </div>
                    )}
                </div>
            )}

            {showRecordModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-md">
                    <div className="bg-brand-surface w-full max-w-lg rounded-3xl border border-gray-800 shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">
                        <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-gray-900/50">
                            <h3 className="text-lg font-black text-white uppercase tracking-tighter">{isPreviewing ? 'Review' : parentSession ? 'Recording Collab' : 'Studio'}</h3>
                            {!isRecording && ( <button onClick={handleCloseModal} className="p-2 hover:bg-gray-800 rounded-full transition-colors"><CloseIcon className="h-6 w-6 text-brand-muted" /></button> )}
                        </div>
                        <div className="flex-1 overflow-y-auto p-2 sm:p-4 space-y-4 custom-scrollbar">
                            <div className="relative rounded-2xl overflow-hidden bg-black shadow-2xl border border-gray-800 flex flex-col w-full max-h-[60vh] sm:max-h-[65vh] aspect-[9/16] mx-auto">
                                <Watermark />
                                {isRecording && ( <div className="absolute top-0 left-0 right-0 h-1 bg-gray-800 z-50"><div className="h-full bg-brand-orange transition-all duration-1000 ease-linear" style={{ width: `${(recordingSeconds / MAX_RECORDING_SECONDS) * 100}%` }} /></div> )}
                                <div className={`flex w-full h-full ${parentSession ? 'flex-col' : ''}`}>
                                    {parentSession && (
                                        <div className="relative h-1/2 bg-black border-b border-gray-800 overflow-hidden">
                                            <video ref={baseVideoRef} src={parentSession.videoUrl} className="w-full h-full object-cover" muted={false} playsInline />
                                            <div className="absolute top-2 right-2 bg-brand-orange px-2 py-1 rounded text-[8px] font-black text-white uppercase shadow-lg">Jam Base</div>
                                        </div>
                                    )}
                                    <div className={`relative ${parentSession ? 'h-1/2' : 'h-full'} bg-black overflow-hidden`}>
                                        {isPreviewing && previewUrl ? ( <video ref={reviewVideoRef} key={previewUrl} src={previewUrl} className="w-full h-full object-cover" controls autoPlay playsInline /> ) : ( <video ref={videoPreviewRef} className="w-full h-full object-cover" autoPlay muted playsInline /> )}
                                    </div>
                                </div>
                                {countdown !== null && ( <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"><span className="text-7xl font-black text-brand-orange animate-ping">{countdown}</span></div> )}
                                {isRecording && ( <div className="absolute top-12 left-1/2 -translate-x-1/2 bg-red-600 px-3 py-1 rounded-full flex items-center gap-2 animate-pulse z-50"><div className="h-2 w-2 bg-white rounded-full" /><span className="text-[10px] font-black text-white font-mono">00:{recordingSeconds.toString().padStart(2, '0')}</span></div> )}
                                {!isPreviewing && countdown === null && ( <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-6 z-10">{!isRecording ? ( <button onClick={handleStartRecording} className="h-16 w-16 bg-white rounded-full flex items-center justify-center hover:scale-105 transition-transform shadow-2xl border-4 border-gray-300/30"><div className="h-7 w-7 bg-red-600 rounded-full" /></button> ) : ( <button onClick={stopRecordingInternal} className="h-16 w-16 bg-red-600 rounded-full flex items-center justify-center hover:scale-105 transition-transform shadow-2xl border-4 border-white/30"><div className="h-7 w-7 bg-white rounded-sm" /></button> )}</div> )}
                            </div>
                            {isPreviewing && (
                                <div className="space-y-4 pb-4 animate-in slide-in-from-bottom-2 duration-500">
                                    {!parentSession && (
                                        <div className="grid grid-cols-2 gap-2">
                                            <button onClick={() => setPerformanceType('solo')} className={`py-2 rounded-xl border font-black text-[9px] uppercase transition-all ${performanceType === 'solo' ? 'bg-brand-orange text-white border-brand-orange shadow-lg' : 'border-gray-700 text-gray-500'}`}>Solo</button>
                                            <button onClick={() => setPerformanceType('base')} className={`py-2 rounded-xl border font-black text-[9px] uppercase transition-all ${performanceType === 'base' ? 'bg-brand-orange text-white border-brand-orange shadow-lg' : 'border-gray-700 text-gray-500'}`}>Open Jam Base</button>
                                        </div>
                                    )}
                                    <div className="space-y-3">
                                        <input value={title} onChange={e => setTitle(e.target.value)} placeholder={parentSession ? `Collab with @${parentSession.creator.handle}` : "Title *"} className="w-full bg-gray-800 text-white rounded-xl p-3 border border-gray-700 font-bold focus:border-brand-orange outline-none text-sm" />
                                        <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (Optional)" className="w-full bg-gray-800 text-white rounded-xl p-3 border border-gray-700 font-bold focus:border-brand-orange outline-none resize-none text-sm" rows={2} />
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={handleRetake} className="flex-1 bg-gray-800 text-white font-black py-3 rounded-xl border border-gray-700 hover:bg-gray-700 text-[10px] uppercase tracking-widest transition-all">Retake</button>
                                        <button onClick={handlePublish} disabled={isUploading || !title.trim()} className="flex-[2] bg-brand-orange text-white font-black py-3 rounded-xl shadow-lg hover:bg-orange-600 disabled:opacity-50 transition-all text-[10px] uppercase tracking-widest">{isUploading ? 'Uploading...' : 'Publish'}</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {playingSession && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md" onClick={() => setPlayingSession(null)}>
                    <div className="relative w-full max-w-lg bg-brand-surface rounded-3xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                        <Watermark />
                        <button onClick={() => setPlayingSession(null)} className="absolute top-4 right-4 z-[120] p-2 bg-black/40 hover:bg-black/60 rounded-full text-white"><CloseIcon className="h-6 w-6" /></button>
                        <div className="relative bg-black overflow-hidden max-h-[60vh] sm:max-h-[65vh] aspect-[9/16] mx-auto w-full">
                            {playingSession.type === 'collab' ? (
                                <div className={`flex w-full h-full ${playingSession.layout === 'split-v' ? 'flex-col' : 'flex-row'}`}>
                                    <div className="relative h-1/2 w-full overflow-hidden"><video src={playingSession.videoUrl} className="w-full h-full object-cover" autoPlay muted loop playsInline /></div>
                                    <div className="relative h-1/2 w-full overflow-hidden"><video src={playingSession.videoUrlB} className="w-full h-full object-cover" autoPlay loop playsInline /></div>
                                </div>
                            ) : ( <video src={playingSession.videoUrl} className="w-full h-full object-cover" autoPlay controls loop playsInline /> )}
                        </div>
                        <div className="p-6 bg-brand-surface flex flex-col gap-4 border-t border-gray-800">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="text-xl font-black text-white uppercase leading-tight">{playingSession.title}</h3>
                                    <p className="text-sm text-gray-300 font-mono tracking-tighter">@{playingSession.creator.handle}</p>
                                </div>
                                <button onClick={(e) => handleShareSession(e, playingSession)} className="p-2 bg-gray-800 rounded-full text-brand-orange hover:bg-gray-700 transition-colors"><ShareIcon className="h-5 w-5" /></button>
                            </div>
                            {playingSession.type === 'base' && (
                                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleJamNow(playingSession); }} className="w-full bg-brand-orange text-white font-black py-4 rounded-2xl text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2 shadow-2xl shadow-brand-orange/40 transition-transform active:scale-95 border border-white/10"><PlusCircleIcon className="h-5 w-5" />Jam Now</button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const SessionCard: React.FC<{ 
    session: Session, 
    onDelete: (e: React.MouseEvent) => void, 
    currentUserHandle?: string, 
    onPlay: () => void,
    onJam?: () => void,
    onShare?: (e: React.MouseEvent) => void
}> = ({ session, onDelete, currentUserHandle, onPlay, onJam, onShare }) => {
    const normalizedMe = currentUserHandle?.toLowerCase();
    const isCreator = normalizedMe === session.creator.handle.toLowerCase() || (session.creatorB && normalizedMe === session.creatorB.handle.toLowerCase());

    return (
        <div className="bg-brand-surface rounded-2xl overflow-hidden border border-gray-800 shadow-lg group hover:border-brand-orange/50 transition-all flex flex-col cursor-pointer relative" onClick={onPlay}>
            <div className="relative aspect-[9/16] bg-black flex items-center justify-center overflow-hidden">
                <video src={session.videoUrl} className="w-full h-full object-cover opacity-60" muted />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10"><div className="bg-white/10 backdrop-blur-md p-4 rounded-full group-hover:scale-110 transition-transform"><PlayIcon className="h-8 w-8 text-white" /></div></div>
                <div className="absolute top-4 left-4 flex flex-col gap-2 z-10">
                    {session.type === 'base' && ( <span className="bg-blue-600 text-white text-[9px] font-black px-2 py-0.5 rounded uppercase tracking-widest shadow-lg">Jam Base</span> )}
                    {session.type === 'collab' && ( <span className="bg-purple-600 text-white text-[9px] font-black px-2 py-0.5 rounded uppercase tracking-widest shadow-lg">Collab</span> )}
                </div>
                <div className="absolute top-4 right-4 flex flex-col gap-2 z-30">
                    {isCreator && ( <button onClick={onDelete} className="p-2.5 bg-red-600/90 hover:bg-red-500 text-white rounded-xl backdrop-blur-sm transition-all shadow-xl border border-white/10"><TrashIcon className="h-4 w-4" /></button> )}
                    <button onClick={onShare} className="p-2.5 bg-brand-dark/60 hover:bg-brand-orange text-white rounded-xl backdrop-blur-sm transition-all shadow-xl border border-white/10"><ShareIcon className="h-4 w-4" /></button>
                </div>
                {session.type === 'base' && !isCreator && (
                    <div className="absolute bottom-4 left-4 right-4 z-30">
                        <button onClick={(e) => { e.stopPropagation(); onJam?.(); }} className="w-full bg-brand-orange text-white font-black py-3 rounded-xl text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 shadow-2xl active:scale-95 transition-transform"><PlusCircleIcon className="h-5 w-5" />Jam Now</button>
                    </div>
                )}
            </div>
            <div className="p-5 border-t border-gray-800">
                <div className="flex items-center gap-2 mb-3">
                    <img src={session.creator.avatarUrl} className="h-8 w-8 rounded-full border border-gray-800 object-cover" alt="" />
                    <span className="text-xs text-brand-muted font-mono truncate">@{session.creator.handle}</span>
                    {session.creatorB && ( <><span className="text-brand-orange text-xs font-black">&</span><span className="text-xs text-brand-muted font-mono truncate">@{session.creatorB.handle}</span></> )}
                </div>
                <h3 className="text-lg font-black text-white uppercase tracking-tight truncate leading-tight">{session.title}</h3>
                {session.description && ( <p className="text-[10px] text-brand-muted mt-2 line-clamp-1 italic">{session.description}</p> )}
            </div>
        </div>
    );
};

export default SessionsPage;